import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { TooltipModule } from 'primeng/tooltip';
import { User, UserRole } from 'src/app/models/User';
import { UserService } from 'src/app/services/user.service';
import { SHARED_IMPORTS } from 'src/app/utils/other/standalone-import';
import * as echarts from 'echarts/core';
import { EChartsOption } from 'echarts';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
echarts.use([
  PieChart,
  GridComponent,
  CanvasRenderer,
  TooltipComponent,
  LegendComponent
]);
@Component({
  selector: 'app-widget-users',
  templateUrl: './widget-users.component.html',
  styleUrls: ['./widget-users.component.scss'],
  standalone: true,
  imports: [SHARED_IMPORTS, TooltipModule, NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })]
})
export class WidgetUsersComponent implements OnInit, OnChanges {
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private router = inject(Router);

  @Input() preview = false;

  @Input() editMode = false;

  @Input() users: User[] = [];

  @Input() colorScheme: 'light' | 'dark' = 'light';

  options!: EChartsOption;

  ngOnInit(): void {
    this.getUsers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users']) {
      this.filterUsers();
      this.generateChartData();
    }
    if (changes['colorScheme']) {
      this.generateChartData();
    }
  }

  /**
   * Filters the list of users to exclude those that have been deleted.
   *
   * @returns { void }
   */
  filterUsers(): void {
    this.users = this.users.filter((user) => user.deletedAt === null);
  }

  /**
   * Fetches all users from the user service, filters the list to exclude deleted users,
   * and generates chart data based on the filtered list.
   *
   * @returns { void }
   */
  getUsers(): void {
    this.userService.findAll().subscribe((users) => {
      this.users = users;
      this.filterUsers();
      this.generateChartData();
    });
  }

  /**
   * Generates chart data for user roles.
   * This method sets up the data and options for a bar chart.
   *
   * @returns { void }
   */
  generateChartData(): void {
    this.options = {
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        position: 'right'
      },
      legend: {
        top: '5%',
        left: 'left',
        orient: 'vertical',
        type: 'scroll',
        textStyle: {
          color: this.colorScheme === 'dark' ? '#fff' : '#000'
        }
      },
      series: [
        {
          name: this.translate.instant('widgets.userRoles.count'),
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['65%', '50%'],
          avoidLabelOverlap: true,
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            },
            label: {
              show: true,
              fontSize: 12,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: [
            {
              value: this.users.filter((user) => user.role === UserRole.Admin)
                .length,
              name: this.translate.instant('widgets.userRoles.labels.Admin'),
              itemStyle: {
                color: 'rgba(69, 168, 198, 0.8)',
                borderColor: 'rgb(69, 168, 198)'
              }
            },
            {
              value: this.users.filter((user) => user.role === UserRole.User)
                .length,
              name: this.translate.instant('widgets.userRoles.labels.User'),
              itemStyle: {
                color: 'rgba(0, 144, 255, 0.8)',
                borderColor: 'rgb(0, 144, 255)'
              }
            }
          ]
        }
      ]
    };
  }

  /**
   * Navigates to the 'users' route to view all users.
   */
  viewAllUsers(): void {
    // Use the router to navigate to the 'users' route
    this.router.navigate(['users']);
  }
}
